import { useCallback, useEffect, useRef, useState } from 'react'
import { useIsMutating } from '@tanstack/react-query'
import { decideUpdate } from '@tfa/shared'
import { hardReload } from '../lib/hardReload'

/**
 * Автообновление Mini App.
 *
 * Telegram НЕ перезагружает свёрнутое приложение при повторном открытии: человек
 * может неделями работать в бандле, загруженном один раз. HTTP-кеш тут не при
 * чём — index.html и так отдаётся с no-store. Из-за этого баг #321 переоткрывали
 * пять раз: репортер снимал видео на сборке без фикса, и понять это было нельзя.
 *
 * Решение: рядом с бандлом лежит /version.json с тем же buildId (пишется
 * vite-плагином из vite.config.ts). Сверяем его со своим на холодном старте, при
 * возврате из фона и раз в 5 минут. Политика решения — чистая decideUpdate()
 * в @tfa/shared (там же L0-тесты).
 */

/** Интервал фоновой проверки, пока приложение видимо. */
const POLL_MS = 5 * 60 * 1000

/** buildId текущего бандла (define в vite.config.ts, фолбэк — 'dev'). */
const CURRENT_BUILD_ID: string | null =
  (import.meta.env.VITE_BUILD_ID as string | undefined) ?? null

/**
 * «Ради этой версии перезагрузку уже пробовали, и она не помогла» — читаем из
 * САМОГО URL: наш reload уходит на `/?v=<buildId>`, значит текущий документ
 * загружен именно им.
 *
 * Раньше признак хранился в sessionStorage и ставился ПЕРЕД навигацией. В
 * Telegram Desktop навигация молча не срабатывала (в логах nginx — только
 * опросы /version.json и ни одного перехода на ?v=), и метка отравляла сессию
 * навсегда: decideUpdate до конца жизни окна возвращал 'banner', а кнопка
 * «Обновить» повторяла тот же безрезультатный вызов. Признак из URL
 * самоочищается: не было перехода — нет и признака, попробуем снова.
 */
function reloadAlreadyTried(serverBuildId: string): boolean {
  try {
    return new URLSearchParams(window.location.search).get('v') === serverBuildId
  } catch {
    return false
  }
}

/** Свежая сборка на сервере. cache:'no-store' — иначе WebView отдаст свою копию. */
async function fetchServerBuildId(): Promise<string | null> {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' })
    if (!res.ok) return null
    const data = (await res.json()) as { buildId?: unknown }
    return typeof data.buildId === 'string' && data.buildId ? data.buildId : null
  } catch {
    return null // офлайн/5xx — просто не знаем версию, приложение не трогаем
  }
}

/**
 * Человек прямо сейчас что-то заполняет — тихо перезагружать нельзя.
 * Проверяем фокус в поле ввода и наличие открытой шторки. Мутации в полёте
 * учитываются отдельно (useIsMutating в самом хуке).
 */
function isEditing(): boolean {
  try {
    const el = document.activeElement as HTMLElement | null
    if (el) {
      const tag = el.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
      if (el.isContentEditable) return true
    }
    // Открытая шторка/модалка: у наших используется data-testid="modal-*"
    // (MetricsEditSheet, MeasurementsSheet, MealSheet и т.д.).
    if (document.querySelector('[data-testid^="modal-"], [role="dialog"]')) return true
    return false
  } catch {
    return false
  }
}

export interface BuildVersionState {
  /** Показать баннер «Доступно обновление». */
  updateReady: boolean
  /** Применить обновление вручную (кнопка в баннере). */
  applyUpdate: () => void
}

export function useBuildVersion(): BuildVersionState {
  const [updateReady, setUpdateReady] = useState(false)
  /** buildId, на который обновляемся — нужен для cache-busting в URL. */
  const targetRef = useRef<string | null>(null)
  const mutating = useIsMutating()
  /** Свежее значение mutating внутрь колбэка без пересоздания эффектов. */
  const mutatingRef = useRef(mutating)
  mutatingRef.current = mutating

  const reloadTo = useCallback((buildId: string) => hardReload(buildId), [])

  const applyUpdate = useCallback(() => {
    reloadTo(targetRef.current ?? String(Date.now()))
  }, [reloadTo])

  const check = useCallback(async () => {
    const server = await fetchServerBuildId()
    if (!server) return
    const action = decideUpdate({
      current: CURRENT_BUILD_ID,
      server,
      busy: mutatingRef.current > 0 || isEditing(),
      reloadAttempted: reloadAlreadyTried(server),
    })
    if (action === 'none') { setUpdateReady(false); return }
    targetRef.current = server
    if (action === 'reload') reloadTo(server)
    else setUpdateReady(true)
  }, [reloadTo])

  useEffect(() => {
    // Локальная сборка без build id — механизм выключен (иначе dev-режим
    // сравнивал бы 'dev' с 'dev' вечно, а при отсутствии значения — шумел).
    if (!CURRENT_BUILD_ID || CURRENT_BUILD_ID === 'dev') return

    void check() // холодный старт

    const onVisible = () => {
      if (document.visibilityState === 'visible') void check()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    // pageshow — единственный надёжный сигнал возврата на мобильных: Telegram
    // морозит WebView целиком (bfcache), таймеры при этом стоят, а
    // visibilitychange/focus на iOS могут не прийти вовсе. Без него приложение,
    // провисевшее свёрнутым, так и оставалось на старой сборке.
    // Проверяем и при persisted=false: холодная проверка дешёвая (один GET).
    window.addEventListener('pageshow', onVisible)

    // Telegram Bot API 8.0+: явные события сворачивания/разворачивания.
    // Дополняют visibilitychange, а не заменяют — на старых клиентах их нет.
    const tg = (window as unknown as {
      Telegram?: { WebApp?: { onEvent?: (e: string, cb: () => void) => void; offEvent?: (e: string, cb: () => void) => void } }
    }).Telegram?.WebApp
    const onActivated = () => { void check() }
    try {
      tg?.onEvent?.('activated', onActivated)
    } catch { /* событие не поддерживается — не страшно */ }

    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void check()
    }, POLL_MS)

    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
      window.removeEventListener('pageshow', onVisible)
      try {
        tg?.offEvent?.('activated', onActivated)
      } catch { /* см. выше */ }
      window.clearInterval(timer)
    }
  }, [check])

  return { updateReady, applyUpdate }
}
